import type { AppWorkerClient } from '../../../../data/AppWorkerClient'
import {
  normalizeCfoDrilldownComparisonKind,
  runCfoDrilldownRecipe,
  type CfoDrilldownServerContext,
} from '../../../../ai/cfoDrilldownRecipes'
import { findCfoDrilldownRecipeForMetric, normalizeCfoDrilldownMetricId } from '../../../../ai/cfoDrilldownManifest'
import type { CfoDrilldownReadyResult, CfoDrilldownRecipeResult } from '../../../../ai/cfoDrilldownTypes'
import { compactMoney, isCfoBridgeDriverNextLevelContext } from './instantCards'
import type { CfoAnalysisContext, CfoInstantCard, CfoInstantCardChangeExplanation } from './types'

// nextLevelDrivers — the true "layer 2" behind a net-income-bridge driver bar.
// Clicking Revenue/COGS/Opex used to re-render the same sibling waterfall the
// user just clicked. Instead the analysis modal calls
// `fetchCfoNextLevelBreakdown` to get what is INSIDE the clicked line —
// segment / service-line direct contribution when rollups are configured
// (revenue + gross-margin, MoM), else statement category → account movement
// from the governed drilldown recipes — and grafts the result onto the
// deterministic instant card via `applyCfoNextLevelBreakdown`. Every row
// carries prior→now values so the card renders the segment-CM-style movement
// grid (Prior · movement dumbbell · Now), not just deltas.

type NextLevelDriver = CfoInstantCardChangeExplanation['drivers'][number]

export type CfoNextLevelBreakdown = {
  source: 'segments' | 'categories' | 'accounts' | 'reconciliation'
  /** Short label rendered as the explanation row label, e.g. "By segment (direct CM)". */
  sourceTitle: string
  recipeId: string | null
  metricLabel: string
  comparison: string
  headline: string
  insight: string
  recommendedAction: string
  drivers: NextLevelDriver[]
  /** Movement-grid column headers derived from the recipe windows. */
  priorLabel: string | null
  currentLabel: string | null
  movementScaleLabel: string | null
  detail: CfoInstantCardChangeExplanation['detail']
  /** Metric-level totals for the explanation row (current/prior/change). */
  totals: { current: string; prior: string; change: string } | null
}

/** Both next-level lenses for a session: statement accounts (any comparison
 *  window) and segment CM (MoM only, needs configured rollups). */
export type CfoNextLevelBreakdowns = {
  accounts: CfoNextLevelBreakdown | null
  segments: CfoNextLevelBreakdown | null
}

const trimStr = (value: unknown): string => String(value ?? '').trim()

const num = (value: unknown, fallback = 0): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const finiteOrNull = (value: unknown): number | null => {
  // Number(null) and Number('') are 0 — a "no data" value must stay null, not
  // become a confident $0/0.0% (an unposted month rendered as real zeros).
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const normalizeKeyPart = (value: unknown): string =>
  trimStr(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'selected'

const signedMoney = (value: number): string => `${value < 0 ? '-' : '+'}${compactMoney(Math.abs(value))}`

const pctShare = (value: number | null | undefined): string =>
  value == null || !Number.isFinite(value) ? '' : `${Math.round(Math.abs(value) * 100)}%`

const pctText = (value: number | null | undefined): string =>
  value == null || !Number.isFinite(value) ? '—' : `${(value * 100).toFixed(1)}%`

const signedPts = (value: number | null | undefined): string =>
  value == null || !Number.isFinite(value) ? '—' : `${value >= 0 ? '+' : '−'}${Math.abs(value * 100).toFixed(1)} pts`

const directionFor = (value: number): NextLevelDriver['direction'] =>
  value > 0.000001 ? 'up' : value < -0.000001 ? 'down' : 'flat'

const clamp01 = (value: number | null): number | null =>
  value == null || !Number.isFinite(value) ? null : Math.max(0, Math.min(1, value))

const withShares = (drivers: NextLevelDriver[]): NextLevelDriver[] => {
  const absSum = drivers.reduce((sum, driver) => sum + Math.abs(Number(driver.rawValue) || 0), 0)
  if (absSum > 0.000001) {
    for (const driver of drivers) driver.shareOfChange = Math.abs(Number(driver.rawValue) || 0) / absSum
  }
  return drivers
}

const DOMINANCE_THRESHOLD = 0.5

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const parseMonthStart = (value: unknown): { year: number; month: number } | null => {
  const match = /^(\d{4})-(\d{2})/.exec(trimStr(value))
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  if (!Number.isFinite(year) || month < 1 || month > 12) return null
  return { year, month }
}

const shortMonth = (ym: { year: number; month: number }): string => `${MONTH_SHORT[ym.month - 1]} ${ym.year}`

/** "Apr 2026" for a single-month window, "Feb–Apr 2026" for a range. */
const windowLabel = (win: { start?: string | null; end_exclusive?: string | null } | null | undefined): string | null => {
  const start = parseMonthStart(win?.start)
  if (!start) return null
  const endEx = parseMonthStart(win?.end_exclusive)
  if (!endEx) return shortMonth(start)
  // Last month INSIDE the window = end_exclusive - 1 month.
  const lastMonth = endEx.month === 1 ? { year: endEx.year - 1, month: 12 } : { year: endEx.year, month: endEx.month - 1 }
  if (lastMonth.year === start.year && lastMonth.month === start.month) return shortMonth(start)
  if (lastMonth.year === start.year) return `${MONTH_SHORT[start.month - 1]}–${shortMonth(lastMonth)}`
  return `${shortMonth(start)}–${shortMonth(lastMonth)}`
}

const priorMonthOf = (value: unknown): string | null => {
  const ym = parseMonthStart(value)
  if (!ym) return null
  return shortMonth(ym.month === 1 ? { year: ym.year - 1, month: 12 } : { year: ym.year, month: ym.month - 1 })
}

const readyWindows = (ready: CfoDrilldownReadyResult): { priorLabel: string | null; currentLabel: string | null } => ({
  priorLabel: windowLabel((ready as any).prior_window),
  currentLabel: windowLabel((ready as any).current_window),
})

/** Metric families that can decompose into segment / service-line direct CM. */
const SEGMENT_METRIC_IDS = new Set(['revenue', 'gross_profit', 'gross_margin', 'gross_margin_pct', 'margin'])
const GROSS_METRIC_IDS = new Set(['gross_profit', 'gross_margin', 'gross_margin_pct', 'margin'])

/** Statement recipes that can serve a real decomposition from the governed
 *  statements render — the set an insight-card click can hydrate drivers from. */
const INSIGHT_NEXT_LEVEL_RECIPE_IDS = new Set([
  'pnl_major_driver_decomposition',
  'statement_line_account_movement',
  'opex_category_account_movement',
  'cash_account_movement',
])

/** Whether the modal should fetch a next-level breakdown for this session. */
export const shouldFetchCfoNextLevelBreakdown = (context: CfoAnalysisContext): boolean => {
  if (isCfoBridgeDriverNextLevelContext(context)) return true
  // Insight-card clicks carry no decomposition of their own (the insight
  // headline is a verdict, not a driver) — fetch the real statement-level
  // drivers whenever the metric maps to a governed statement recipe.
  if (trimStr(context.sourceComponent) !== 'insight_card') return false
  const recipe = findCfoDrilldownRecipeForMetric(context.metricId, 'insight_card')
  return !!recipe && INSIGHT_NEXT_LEVEL_RECIPE_IDS.has(recipe.id)
}

const breakdownFromSegmentRecipe = (
  result: CfoDrilldownRecipeResult,
  metricLabel: string,
  surface: string | null,
): CfoNextLevelBreakdown | null => {
  if (result.status !== 'ready') return null
  const ready = result as CfoDrilldownReadyResult
  // finiteOrNull (not Number.isFinite(Number(...))) so null-delta rows — segments with
  // no computable MoM movement, e.g. the anchor month is unposted — drop out instead of
  // rendering as $0. All-null rows leave < 2, which suppresses the segments lens entirely.
  const rows = (ready.category_rows || []).filter((row) => finiteOrNull(row?.contribution_delta_abs) != null)
  // A single configured segment is not a breakdown — fall back to categories.
  if (rows.length < 2) return null
  const drivers = withShares(
    rows.slice(0, 6).map((row): NextLevelDriver => {
      const delta = num(row.contribution_delta_abs)
      const cmCurrent = finiteOrNull(row.cm_pct_current)
      const cmPrior = finiteOrNull(row.cm_pct_prior)
      const contributionCurrent = finiteOrNull(row.contribution_current)
      const contributionPrior = finiteOrNull(row.contribution_prior)
      const label = trimStr(row.name) || trimStr(row.rollup_id) || 'Segment'
      return {
        id: `segment:${trimStr(row.rollup_id) || normalizeKeyPart(label)}`,
        label,
        value: signedMoney(delta),
        rawValue: delta,
        shareOfChange: null,
        direction: directionFor(delta),
        category: 'segment',
        sourceSurface: surface,
        target: { surface, metricId: 'revenue' },
        priorDisplay: contributionPrior != null ? compactMoney(contributionPrior) : '—',
        currentDisplay: contributionCurrent != null ? compactMoney(contributionCurrent) : '—',
        plotPrior: clamp01(cmPrior),
        plotCurrent: clamp01(cmCurrent),
        subLabel: cmPrior != null || cmCurrent != null ? `CM ${pctText(cmPrior)} → ${pctText(cmCurrent)}` : null,
      }
    }),
  )
  const top = drivers[0]
  if (!top) return null
  const second = drivers[1] || null
  const dominant = top.shareOfChange != null && top.shareOfChange >= DOMINANCE_THRESHOLD
  const headline = dominant
    ? `${top.label} drove ~${pctShare(top.shareOfChange)} of the segment contribution move (${signedMoney(num(top.rawValue))}).`
    : `${top.label} (${signedMoney(num(top.rawValue))}) led the segment contribution move${second ? `; ${second.label} was next (${signedMoney(num(second.rawValue))})` : ''}.`
  const asOf = trimStr((ready as any).as_of) || null
  const currentLabel = windowLabel({ start: asOf }) || 'Latest month'
  return {
    source: 'segments',
    sourceTitle: 'By segment (direct CM)',
    recipeId: ready.recipe_id,
    metricLabel,
    comparison: 'mom',
    headline,
    insight: `Direct contribution by service line: ${headline.replace(/\.$/, '')} — contribution nets each segment's revenue against its paired direct COGS.`,
    recommendedAction: `Break down ${top.label} revenue and direct COGS movement vs. prior month to confirm whether the swing is volume, price, or cost before changing the plan.`,
    drivers,
    priorLabel: priorMonthOf(asOf) || 'Prior month',
    currentLabel,
    movementScaleLabel: 'Dots plot direct CM % of segment revenue',
    detail: null,
    totals: null,
  }
}

/** Gross profit / gross margin — the decomposition is Revenue − COGS = Gross
 *  profit, plotted as % of revenue so the margin move is literally visible.
 *  OpEx never appears: it cannot move a gross metric. */
const breakdownFromGrossRecipe = (
  result: CfoDrilldownRecipeResult,
  metricLabel: string,
  surface: string | null,
  comparison: string,
): CfoNextLevelBreakdown | null => {
  if (result.status !== 'ready') return null
  const ready = result as CfoDrilldownReadyResult
  if (ready.recipe_id !== 'pnl_major_driver_decomposition') return null
  const rows = Array.isArray(ready.driver_rows) ? ready.driver_rows : []
  const revenue = rows.find((row: any) => trimStr(row.label) === 'Revenue')
  const cogs = rows.find((row: any) => trimStr(row.label) === 'COGS')
  if (!revenue) return null
  const revCur = num((revenue as any).current_value)
  const revPrior = num((revenue as any).prior_value)
  const cogsCur = num((cogs as any)?.current_value)
  const cogsPrior = num((cogs as any)?.prior_value)
  if (Math.abs(revCur) < 0.005 && Math.abs(revPrior) < 0.005) return null
  const gpCur = revCur - cogsCur
  const gpPrior = revPrior - cogsPrior
  const marginCur = Math.abs(revCur) > 0.005 ? gpCur / revCur : null
  const marginPrior = Math.abs(revPrior) > 0.005 ? gpPrior / revPrior : null
  const marginDelta = marginCur != null && marginPrior != null ? marginCur - marginPrior : null
  const cogsShareCur = Math.abs(revCur) > 0.005 ? cogsCur / revCur : null
  const cogsSharePrior = Math.abs(revPrior) > 0.005 ? cogsPrior / revPrior : null
  const { priorLabel, currentLabel } = readyWindows(ready)
  const revDelta = revCur - revPrior
  const cogsDelta = cogsCur - cogsPrior
  const gpDelta = gpCur - gpPrior
  const drivers: NextLevelDriver[] = [
    {
      id: 'gross:revenue',
      label: 'Revenue',
      value: signedMoney(revDelta),
      rawValue: revDelta,
      shareOfChange: null,
      direction: directionFor(revDelta),
      category: 'gross_reconciliation',
      sourceSurface: surface,
      target: { surface, metricId: 'revenue' },
      priorDisplay: compactMoney(revPrior),
      currentDisplay: compactMoney(revCur),
      plotPrior: 1,
      plotCurrent: 1,
    },
    {
      id: 'gross:cogs',
      label: '− COGS',
      value: signedMoney(cogsDelta),
      // rawValue is the NI/GP effect so shares and coloring read correctly:
      // COGS falling is a positive gross-profit effect.
      rawValue: -cogsDelta,
      shareOfChange: null,
      direction: directionFor(-cogsDelta),
      category: 'gross_reconciliation',
      sourceSurface: surface,
      target: { surface, metricId: 'cost_of_sales' },
      priorDisplay: compactMoney(cogsPrior),
      currentDisplay: compactMoney(cogsCur),
      plotPrior: clamp01(cogsSharePrior),
      plotCurrent: clamp01(cogsShareCur),
      subLabel: cogsSharePrior != null || cogsShareCur != null ? `${pctText(cogsSharePrior)} → ${pctText(cogsShareCur)} of revenue` : null,
    },
    {
      id: 'gross:gross_profit',
      label: '= Gross profit',
      value: `${signedMoney(gpDelta)}${marginDelta != null ? ` · ${signedPts(marginDelta)}` : ''}`,
      rawValue: gpDelta,
      shareOfChange: null,
      direction: directionFor(gpDelta),
      category: 'gross_reconciliation',
      sourceSurface: surface,
      target: { surface, metricId: 'gross_profit' },
      priorDisplay: compactMoney(gpPrior),
      currentDisplay: compactMoney(gpCur),
      plotPrior: clamp01(marginPrior),
      plotCurrent: clamp01(marginCur),
      subLabel: marginPrior != null || marginCur != null ? `margin ${pctText(marginPrior)} → ${pctText(marginCur)}` : null,
      emphasis: 'total',
    },
  ]
  // Account movers inside COGS (server recipe attaches bucketed account rows).
  const accountRows = Array.isArray(ready.account_rows) ? ready.account_rows : []
  const cogsMovers = accountRows
    .filter((row: any) => {
      const bucketId = trimStr(row?.bucket?.id)
      if (bucketId) return bucketId === 'cost_of_sales'
      return /cogs|cost of (goods|sales)/i.test(trimStr(row?.category_label))
    })
    .slice(0, 4)
    .map((row: any): NextLevelDriver => {
      const delta = num(row.delta_abs, num(row.current_value) - num(row.prior_value))
      const label = trimStr(row.account_label) || 'Account'
      return {
        id: `account:${trimStr(row.account_id) || normalizeKeyPart(label)}`,
        label,
        value: signedMoney(delta),
        rawValue: -delta,
        shareOfChange: null,
        direction: directionFor(-delta),
        category: 'account',
        sourceSurface: surface,
        target: { surface, metricId: 'cost_of_sales', accountId: trimStr(row.account_id) || null },
        priorDisplay: compactMoney(num(row.prior_value)),
        currentDisplay: compactMoney(num(row.current_value)),
      }
    })
  const cogsMoved = Math.abs(cogsDelta) > Math.abs(revDelta)
  const headline = marginDelta != null
    ? `Gross margin moved ${signedPts(marginDelta)} to ${pctText(marginCur)} — ${cogsMoved ? `a COGS effect: COGS moved ${signedMoney(cogsDelta)} while revenue moved ${signedMoney(revDelta)}` : `a revenue effect: revenue moved ${signedMoney(revDelta)} while COGS moved ${signedMoney(cogsDelta)}`}.`
    : `Gross profit moved ${signedMoney(gpDelta)} (revenue ${signedMoney(revDelta)}, COGS ${signedMoney(cogsDelta)}).`
  const topCogs = cogsMovers[0] || null
  return {
    source: 'reconciliation',
    sourceTitle: 'Revenue − COGS = Gross profit',
    recipeId: ready.recipe_id,
    metricLabel,
    comparison,
    headline,
    insight: headline,
    recommendedAction: topCogs
      ? `Check the transactions behind ${topCogs.label} (${topCogs.value}) to confirm the COGS move is complete and real before reading this as margin.`
      : `Verify COGS posting is complete for the period before reading this as real margin.`,
    drivers,
    priorLabel,
    currentLabel,
    movementScaleLabel: 'Dots plot % of revenue',
    detail: cogsMovers.length ? { label: 'What moved inside COGS', drivers: cogsMovers } : null,
    totals: {
      current: marginCur != null ? pctText(marginCur) : compactMoney(gpCur),
      prior: marginPrior != null ? pctText(marginPrior) : compactMoney(gpPrior),
      change: marginDelta != null ? signedPts(marginDelta) : signedMoney(gpDelta),
    },
  }
}

/** Major P&L driver decomposition (Revenue / COGS / OpEx buckets) — the right
 *  altitude for a total-profit metric like net income. */
const breakdownFromPnlDriverRecipe = (
  result: CfoDrilldownRecipeResult,
  metricId: string,
  metricLabel: string,
  surface: string | null,
  comparison: string,
): CfoNextLevelBreakdown | null => {
  if (result.status !== 'ready') return null
  const ready = result as CfoDrilldownReadyResult
  // driver_rows only carry the Revenue/COGS/OpEx altitude on the pnl recipe;
  // other recipes stay on the category/account path below.
  if (ready.recipe_id !== 'pnl_major_driver_decomposition') return null
  if (GROSS_METRIC_IDS.has(metricId)) {
    const gross = breakdownFromGrossRecipe(result, metricLabel, surface, comparison)
    if (gross) return gross
  }
  const rows = (Array.isArray(ready.driver_rows) ? ready.driver_rows : [])
    .filter((row: any) => Number.isFinite(Number(row?.net_income_effect)))
    .slice(0, 6)
  if (!rows.length) return null
  const maxAbs = Math.max(
    ...rows.flatMap((row: any) => [Math.abs(num(row.current_value)), Math.abs(num(row.prior_value))]),
    1,
  )
  const drivers = withShares(
    rows.map((row: any, index: number): NextLevelDriver => {
      const effect = num(row.net_income_effect, num(row.delta_abs))
      const label = trimStr(row.label) || `Driver ${index + 1}`
      return {
        id: `pnl_driver:${normalizeKeyPart(label)}`,
        label,
        value: signedMoney(effect),
        rawValue: effect,
        shareOfChange: null,
        direction: directionFor(effect),
        category: 'major_pnl_driver',
        sourceSurface: surface,
        target: { surface, metricId: normalizeKeyPart(label) },
        priorDisplay: compactMoney(num(row.prior_value)),
        currentDisplay: compactMoney(num(row.current_value)),
        plotPrior: clamp01(Math.abs(num(row.prior_value)) / maxAbs),
        plotCurrent: clamp01(Math.abs(num(row.current_value)) / maxAbs),
      }
    }),
  )
  const top = drivers[0]
  if (!top) return null
  const second = drivers[1] || null
  const dominant = top.shareOfChange != null && top.shareOfChange >= DOMINANCE_THRESHOLD
  const { priorLabel, currentLabel } = readyWindows(ready)
  const headline = dominant
    ? `${top.label} drove ~${pctShare(top.shareOfChange)} of the ${metricLabel} move (${top.value} NI effect).`
    : `${top.label} (${top.value}) was the largest driver of the ${metricLabel} move${second ? `; ${second.label} was next (${second.value})` : ''}.`
  return {
    source: 'categories',
    sourceTitle: 'By P&L driver',
    recipeId: ready.recipe_id,
    metricLabel,
    comparison,
    headline,
    insight: headline,
    recommendedAction: `Break ${top.label} down to confirm whether the ${top.value} swing is one account/customer or broad before changing the plan.`,
    drivers,
    priorLabel,
    currentLabel,
    movementScaleLabel: `Dots plot $ on a shared scale to ${compactMoney(maxAbs)}`,
    detail: null,
    totals: null,
  }
}

const breakdownFromStatementRecipe = (
  result: CfoDrilldownRecipeResult,
  metricId: string,
  metricLabel: string,
  surface: string | null,
  comparison: string,
): CfoNextLevelBreakdown | null => {
  if (result.status !== 'ready') return null
  const ready = result as CfoDrilldownReadyResult
  const categories = Array.isArray(ready.category_rows) ? ready.category_rows : []
  const accounts = Array.isArray(ready.account_rows) ? ready.account_rows : []
  // Categories are the right altitude when the statement actually has more
  // than one; a single bucket means the granularity lives at the accounts.
  const useCategories = categories.length >= 2
  const rows = (useCategories ? categories : accounts).slice(0, 6)
  if (!rows.length) return null
  const maxAbs = Math.max(
    ...rows.flatMap((row: any) => [Math.abs(num(row.current_value)), Math.abs(num(row.prior_value))]),
    1,
  )
  const totalCurrent = rows.reduce((sum: number, row: any) => sum + num(row.current_value), 0)
  const totalPrior = rows.reduce((sum: number, row: any) => sum + num(row.prior_value), 0)
  const drivers = withShares(
    rows.map((row, index): NextLevelDriver => {
      const effect = num(row.net_income_effect, num(row.delta_abs))
      const label = trimStr(useCategories ? row.category_label : row.account_label) || `Line ${index + 1}`
      const shareCur = Math.abs(totalCurrent) > 0.005 ? num(row.current_value) / totalCurrent : null
      const sharePrior = Math.abs(totalPrior) > 0.005 ? num(row.prior_value) / totalPrior : null
      return {
        id: useCategories
          ? `category:${normalizeKeyPart(row.category_id ?? label)}`
          : `account:${trimStr(row.account_id) || normalizeKeyPart(label)}`,
        label,
        value: signedMoney(effect),
        rawValue: effect,
        shareOfChange: null,
        direction: directionFor(effect),
        category: useCategories ? 'statement_category' : 'account',
        sourceSurface: surface,
        target: {
          surface,
          metricId,
          accountId: useCategories ? null : trimStr(row.account_id) || null,
        },
        priorDisplay: compactMoney(num(row.prior_value)),
        currentDisplay: compactMoney(num(row.current_value)),
        plotPrior: clamp01(Math.abs(num(row.prior_value)) / maxAbs),
        plotCurrent: clamp01(Math.abs(num(row.current_value)) / maxAbs),
        subLabel:
          sharePrior != null && shareCur != null && metricId === 'revenue'
            ? `${pctText(sharePrior)} → ${pctText(shareCur)} of revenue`
            : null,
      }
    }),
  )
  const top = drivers[0]
  if (!top) return null
  const second = drivers[1] || null
  const dominant = top.shareOfChange != null && top.shareOfChange >= DOMINANCE_THRESHOLD
  const scope = useCategories ? 'category' : 'account'
  const { priorLabel, currentLabel } = readyWindows(ready)
  const headline = dominant
    ? `${top.label} drove ~${pctShare(top.shareOfChange)} of the ${metricLabel} move (${top.value} NI effect).`
    : `${top.label} (${top.value}) was the largest ${scope}-level driver of the ${metricLabel} move${second ? `; ${second.label} was next (${second.value})` : ''}.`
  return {
    source: useCategories ? 'categories' : 'accounts',
    sourceTitle: useCategories ? 'By statement category' : 'By account',
    recipeId: ready.recipe_id,
    metricLabel,
    comparison,
    headline,
    insight: headline,
    recommendedAction: useCategories
      ? `Open the accounts inside ${top.label} to confirm whether the move is one account or broad before changing the forecast.`
      : `Check the transactions behind ${top.label} to confirm whether the move is recurring, timing-related, or one-off.`,
    drivers,
    priorLabel,
    currentLabel,
    movementScaleLabel: `Dots plot $ on a shared scale to ${compactMoney(maxAbs)}`,
    detail: null,
    totals: {
      current: compactMoney(totalCurrent),
      prior: compactMoney(totalPrior),
      change: signedMoney(totalCurrent - totalPrior),
    },
  }
}

/**
 * Fetch the next-level decomposition for a clicked bridge driver or insight
 * card. Returns BOTH lenses when available: `accounts` (statement recipe at the
 * requested comparison window — category → account movement, gross metrics get
 * the Revenue − COGS = Gross profit reconciliation) and `segments` (governed
 * service-line direct-CM recipe; MoM only, needs configured rollups). Both
 * serve from governed server contracts when local DuckDB isn't warmed — so
 * this works on the lightweight Home page. `accounts: null` means no real
 * breakdown is available (the card keeps its honest "drivers warming"
 * skeleton); `segments: null` means the toggle should not be offered.
 */
export async function fetchCfoNextLevelBreakdown(opts: {
  appApi: CfoDrilldownServerContext['appApi']
  workerClient?: AppWorkerClient | null
  duckDbOpts?: Record<string, any> | null
  workbookId: string | null | undefined
  context: CfoAnalysisContext
  /** Override the comparison window (modal period switcher). Defaults to the
   *  session's comparison, else MoM. */
  comparison?: string | null
}): Promise<CfoNextLevelBreakdowns> {
  const { appApi, workerClient = null, duckDbOpts = null, workbookId, context } = opts
  const metricId = normalizeCfoDrilldownMetricId(context.metricId)
  const metricLabel = trimStr(context.metricLabel) || 'Selected metric'
  const surface = trimStr(context.activeSection) || trimStr(context.pageId) || null
  const asOfHint = trimStr(context.period) || null
  const comparison =
    normalizeCfoDrilldownComparisonKind(opts.comparison) ??
    normalizeCfoDrilldownComparisonKind(context.comparison) ??
    'mom'
  const args = {
    metric_id: metricId,
    metric_label: metricLabel,
    comparison,
    as_of: asOfHint || undefined,
    source_component: trimStr(context.sourceComponent) || 'net_income_bridge_bar',
  }
  const serverCtx: CfoDrilldownServerContext = { appApi, workbookId: workbookId ?? null }
  // The segment recipe compares latest vs prior month only, so offer it just
  // for MoM — and only for the metrics segment CM actually decomposes
  // (revenue, and gross profit/margin = revenue − direct COGS).
  const segmentEligible = SEGMENT_METRIC_IDS.has(metricId) && comparison === 'mom'
  const [segment, statement] = await Promise.all([
    segmentEligible
      ? runCfoDrilldownRecipe(workerClient, duckDbOpts, asOfHint, { ...args, recipe_id: 'segment_contribution_movement' }, serverCtx).catch(
          (): CfoDrilldownRecipeResult | null => null,
        )
      : Promise.resolve<CfoDrilldownRecipeResult | null>(null),
    runCfoDrilldownRecipe(workerClient, duckDbOpts, asOfHint, args, serverCtx).catch(
      (): CfoDrilldownRecipeResult | null => null,
    ),
  ])
  const accounts =
    (statement ? breakdownFromPnlDriverRecipe(statement, metricId, metricLabel, surface, comparison) : null) ||
    (statement ? breakdownFromStatementRecipe(statement, metricId, metricLabel, surface, comparison) : null)
  const segments = segment ? breakdownFromSegmentRecipe(segment, metricLabel, surface) : null
  return { accounts, segments }
}

/**
 * Graft a fetched next-level breakdown onto the deterministic instant card:
 * the ranked driver list becomes what's INSIDE the clicked line (rendered as
 * the prior→now movement grid when rows carry both endpoints), the verdict
 * gains the dominant next-level cause, and the redundant hero bridge stays off.
 */
export function applyCfoNextLevelBreakdown(card: CfoInstantCard, breakdown: CfoNextLevelBreakdown): CfoInstantCard {
  if (!breakdown.drivers.length) return card
  const currentRow = (card.comparisons || []).find((row) => row.label === 'Current month')
  const explanation: CfoInstantCardChangeExplanation = {
    periodKind: 'current_month',
    label: breakdown.sourceTitle,
    status: 'ready',
    current: breakdown.totals?.current || trimStr(currentRow?.current) || '-',
    prior: breakdown.totals?.prior || trimStr(currentRow?.prior) || '-',
    change: breakdown.totals?.change || trimStr(currentRow?.change) || '-',
    headline: breakdown.headline,
    drivers: breakdown.drivers,
    confidence: 'high',
    recommendedAction: breakdown.recommendedAction,
    priorLabel: breakdown.priorLabel,
    currentLabel: breakdown.currentLabel,
    movementScaleLabel: breakdown.movementScaleLabel,
    detail: breakdown.detail,
  }
  const top = breakdown.drivers[0]
  const share = top?.shareOfChange ?? null
  const dominantClause =
    top && share != null && share >= DOMINANCE_THRESHOLD ? ` — ${top.label} drove ~${pctShare(share)} of it` : ''
  const baseHeadline = trimStr(card.verdict?.headline).replace(/[.\s]+$/, '')
  const verdict =
    card.verdict && baseHeadline
      ? { ...card.verdict, headline: `${baseHeadline}${dominantClause}.` }
      : card.verdict ?? null
  return {
    ...card,
    bridge: null,
    verdict,
    changeExplanations: [explanation],
    insights: [breakdown.insight],
    prescriptiveActions: [breakdown.recommendedAction],
  }
}
